import { useEffect, useEffectEvent, useCallback, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import Tooltip from '@mui/material/Tooltip';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCircleXmark } from "@fortawesome/free-solid-svg-icons";
import { DateTime } from "luxon";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import { StoppageDto } from "../../DTOs/Stoppage/StoppageDto";
import { detectionSourceLabel, isAutoDetected } from "../../DTOs/Common/DetectionSource";
import { VerificationIndicator } from "../../components/VerificationIndicator/VerificationIndicator";
import { DataTable, TableRow } from "../../components/DataTable/DataTable";
import TablePage from "../../components/Layout/TablePage";
import { DataTableFilteredRequest, useDataTable } from "../../components/DataTable/TableHook";
import { useAutoPageSize } from "../../components/Hooks/useAutoPageSize";
import { setCurrentView } from "../../state/reducers/viewReducer";
import { RootState } from "../../state/store";
import { stoppagesAjax } from "../../ajax/StoppagesAjax";
import { stoppagesMutations, wasQueued } from "../../offline/stoppagesMutations";
import { saveStoppage } from "./saveStoppage";
import { cachedPlantFor, patchCachedOperationAvailability } from "../../offline/patchCachedStoppages";
import { ErrorResponse } from "../../DTOs/Common/ErrorResponse";
import { ZoneMachineCell } from "../../components/ZoneChip/ZoneMachineCell";
import TimeCounter from "../../components/TimeCounter/TimeCounter";
import { DateSelectCrumb } from "../../components/Breadcrumbs/DateSelectCrumb";
import { ShiftSelectCrumb } from "../../components/Breadcrumbs/Shift/ShiftSelectCrumb";
import { ButtonCrumb } from "../../components/Breadcrumbs/ButtonCrumb";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { setSelectedDate, setSelectedShift } from "../../state/reducers/shiftReducer";
import useShiftTimes from "../../components/Hooks/useShiftTimes";
import { LocationDTO } from "../../DTOs/Production/LocationDTO";
import { referencesAjax } from "../../ajax/ReferencesAjax";
import { useOptions } from "../../components/Hooks/useOptions";
import { useSignalRContext } from "../../components/Contexts/SignalRContext";
import StoppageStatusCell from "./TableCellsComponents/StoppageStatusCell";
import UploadStoppageImageButton from "./TableCellsComponents/UploadStoppageImageButton";
import ViewStoppageImagesButton from "./TableCellsComponents/ViewStoppageImagesButton";
import BreakdownCodeCell from "./TableCellsComponents/BreakdownCodeCell";
import StoppageLocationCell from "./TableCellsComponents/StoppageLocationCell";
import RepairsFaultsCell from "./TableCellsComponents/RepairsFaultsCell";
import WorkOrderCell from "./TableCellsComponents/WorkOrderCell";
import WhoWorkedOnItCell from "./TableCellsComponents/WhoWorkedOnItCell";
import StoppagesTableHeader from "./StoppagesTableHeader";
import { ZoneDTO } from "../../DTOs/ZoneDTO";
import { Option } from "../../DTOs/Common/OptionResponse";
import { filterCachedRows, matchesText } from "../../offline/filterCachedRows";
import { isOnlineNow } from "../../offline/connectivity";
import { Cancelled } from "../../types/ErrorTypes";
import { useScrollAndPulseRow } from "../../components/Hooks/useScrollAndPulseRow";
import { useTelemetryEnabled } from "../../components/Hooks/useTelemetryEnabled";
import {
  STOPPAGES_DEEP_LINK_PARAMS,
  hasStoppagesDeepLink,
  readStoppagesDeepLink,
} from "../../utilities/helpers/stoppagesDeepLink";
import { stoppagesListQueryKey, unfilteredStoppagesListCacheKey } from "../../offline/stoppagesListCacheKey";
import { useIsReadOnly } from "../../utilities/helpers/roleUtilities";
import i18n from "../../i18n";
import { vocabularyName } from "../../i18n/vocabulary";
import { formatDate } from "../../utilities/format";

const Stoppages: React.FC = () => {
  const { t } = useTranslation("stoppages");
  const isReadOnly = useIsReadOnly();
  const currentView = useSelector((state: RootState) => state.view.currentView);
  const dispatch = useDispatch();
  const { connection } = useSignalRContext();
  const { enqueueSnackbar } = useSnackbar();
  const { shiftStartTime, shiftEndTime, selectedDate, selectedShift } = useShiftTimes();
  const [selectedStatus, setSelectedStatus] = useState<string>("Down & Up");
  const [selectedJobType, setSelectedJobType] = useState<{ value: number; label: string } | null>(null);
  const [machineFilter, setMachineFilter] = useState<string>("");
  const [selectedZone, setSelectedZone] = useState<ZoneDTO | null>(null);
  const telemetryEnabled = useTelemetryEnabled();
  const [unverifiedOnly, setUnverifiedOnly] = useState(false);
  const [highlightedStoppageId, setHighlightedStoppageId] = useState<number | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const link = readStoppagesDeepLink(searchParams);
    if (!hasStoppagesDeepLink(link)) return;

    if (link.date) dispatch(setSelectedDate(link.date));
    if (link.shift) dispatch(setSelectedShift(link.shift));
    if (link.machine !== null) setMachineFilter(link.machine);
    if (link.stoppageId) setHighlightedStoppageId(link.stoppageId);

    const remaining = new URLSearchParams(searchParams);
    STOPPAGES_DEEP_LINK_PARAMS.forEach(key => remaining.delete(key));
    setSearchParams(remaining, { replace: true });
  }, [searchParams, setSearchParams, dispatch]);


  const {
    options: destinationOptions,
    isLoading: isDestinationOptionsLoading
  } = useOptions<LocationDTO, LocationDTO>(
    ['references', 'locations'],
    () => referencesAjax.getLocations({}),
    x => x,
  )

  const { options: jobTypes, isLoading: isJobTypesLoading } = useOptions<Option<number>, { value: number; label: string }>(
    ['references', 'jobTypes'],
    () => referencesAjax.getJobTypes(),
    o => ({ value: o.key, label: vocabularyName("jobTypes", o.label) }),
  );

  const {
    options: zoneOptions,
    isLoading: isZoneOptionsLoading
  } = useOptions<ZoneDTO, ZoneDTO>(
    ['references', 'zones'],
    () => referencesAjax.getZones({ pageNumber: 1, pageSize: 100 }),
    x => x,
  )

  useEffect(() => {
    const newView = "Stoppages";

    if (currentView !== newView) {
      dispatch(setCurrentView(newView));
    }
  }, [currentView, dispatch]);

  const getRows = useCallback(async (request: DataTableFilteredRequest) => {
    const filters = {
      status: selectedStatus === "Down & Up" ? "" : selectedStatus,
      jobType: selectedJobType?.value,
      machineName: machineFilter,
      zoneId: selectedZone?.id,
      unverifiedOnly
    };

    const fromCache = () => {
      return filterCachedRows<StoppageDto>(
        unfilteredStoppagesListCacheKey(selectedDate.toISODate(), selectedShift),
        row => (filters.status !== 'Down' || row.endTime == null)
          && matchesText((row.plant ?? cachedPlantFor(row.plantId))?.plantRef, filters.machineName)
          && (!filters.zoneId || row.plant?.zone?.id === filters.zoneId)
          && (!filters.unverifiedOnly || row.isVerified === false),
        {
          field: request.sortField,
          direction: request.sortDirection,
          accessors: {
            Machine: row => (row.plant ?? cachedPlantFor(row.plantId))?.plantRef,
            'Date In': row => row.startTime,
            'Time In': row => row.startTime,
            'Time Out': row => row.endTime,
          },
          fallback: row => row.startTime,
          fallbackDirection: 'desc',
        },
      );
    };

    if (!isOnlineNow()) return fromCache();

    const result = await stoppagesAjax.getStoppagesMaintenance({
      ...request,
      shiftDate: selectedDate.toISODate(),
      dayOrNight: selectedShift[0],
      ...filters
    } as DataTableFilteredRequest).catch((error: unknown) => {
      if (error === Cancelled) throw error;
      return null;
    });
    return result && !ErrorResponse.isErrorResponse(result) ? result : fromCache();
  }, [selectedStatus, selectedJobType?.value, machineFilter, selectedZone?.id, unverifiedOnly, selectedDate, selectedShift]);

  const { pageSize: fitPageSize, onAutoPageSize } = useAutoPageSize();

  const {
    rows,
    fetchedRows,
    isLoading,
    isBackgroundFetching,
    error,
    pageNumber,
    sortField,
    sortDirection,
    setPage,
    pageSize,
    totalCount,
    updateRowSource,
    appendRowSource,
    removeRowSource
  } = useDataTable<StoppageDto>({
    queryKey: stoppagesListQueryKey(selectedDate.toISODate(), selectedShift, {
      status: selectedStatus,
      jobType: selectedJobType?.value ?? null,
      machineName: machineFilter,
      zoneId: selectedZone?.id ?? null,
      unverifiedOnly,
    }),
    fetchFn: getRows,
    initialPage: 1,
    pageSize: fitPageSize,
    clientPaginate: true,
  });

  const saveRow = useCallback(
    (stoppage: StoppageDto) => saveStoppage(stoppage, { enqueueSnackbar, updateRowSource }),
    [enqueueSnackbar, updateRowSource]);

  const handleLocationChange = useCallback(
    (stoppage: StoppageDto, locationId: number) => saveRow({ ...stoppage, locationId }),
    [saveRow]);

  const handleStoppageSubTypeChange = useCallback(async (stoppage: StoppageDto, stoppageSubTypeId?: number) => {
    if (!stoppageSubTypeId) {
      enqueueSnackbar(t("messages.invalidSubType"), { variant: 'error', autoHideDuration: 3000 });
      return;
    }
    await saveRow({ ...stoppage, stoppageSubTypeId });
  }, [enqueueSnackbar, saveRow, t]);

  const setAsAvailable = useCallback(async (stoppage: StoppageDto) => {
    let isValid = true;

    if (!stoppage.stoppageSubTypeId) {
      enqueueSnackbar(t("messages.invalidStoppage"), { variant: 'error', autoHideDuration: 3000 });
      isValid = false;
    }
    if (!isValid)
      return false;

    const response = await stoppagesMutations.updateStoppage(stoppage);
    if (!response.success && !response.data) {
      response.errors?.forEach((error, index) => {
        enqueueSnackbar(error.description, { variant: 'error', autoHideDuration: 5000 + index * 1500 })
      })

      return false;
    }

    enqueueSnackbar(t("messages.statusUpdated"), { variant: 'success', autoHideDuration: 3000 })
    updateRowSource(oldStoppage => oldStoppage.stoppageId === stoppage.stoppageId, response.data!);

    if (wasQueued(response) && stoppage.plantId) {
      patchCachedOperationAvailability(stoppage.plantId, selectedDate.toISODate()!, selectedShift, stoppage.stoppageId);
    }

    return true;
  }, [enqueueSnackbar, selectedDate, selectedShift, updateRowSource, t]);

  const handleStoppageImageStateUpdated = useCallback((updated: StoppageDto) => {
    if (!updated.stoppageId) return;
    updateRowSource(old => old.stoppageId === updated.stoppageId, updated);
  }, [updateRowSource]);

  const translateRows = useCallback((stoppage: StoppageDto): TableRow => {
    const plant = stoppage.plant?.zone
      ? stoppage.plant
      : cachedPlantFor(stoppage.plantId) ?? stoppage.plant;
    return {
      id: stoppage.stoppageId || 0,
      rowContainerTailwindClasses: 'animate-none', // Note: entry animation disabled for ExpendableTextAreas to work properly
      values: [
        {
          name: 'Status',
          label: i18n.t("fields.status"),
          type: 'custom',
          value: stoppage.endTime ? "UP" : "DOWN",
          tailwindClasses: `flex items-center justify-center w-full basis-[4%]`,
          render: (tableValue) => {
            return (
              <Tooltip
                arrow
                placement="top"
                title={tableValue.value === "UP" ? t("list.available") : vocabularyName("stoppageTypes", stoppage.stoppageType?.stoppageType) ?? ''}
                className="w-full"
              >
                <span
                  style={{
                    backgroundColor: tableValue.value === "UP" ? "#44cc44" : stoppage.stoppageType?.colourHex
                  }}
                  className={`px-2 py-4 text-white font-bold h-full inline-flex w-full items-center justify-center`}>
                  {tableValue.value === "UP" ? t("list.up") : t("list.down")}
                </span>
              </Tooltip>
            );
          },
        },
        {
          name: "Set Avail'", label: t("columns.setAvailable"), type: 'custom', value: 'custom', tailwindClasses: 'w-full basis-[3%]',
          render: () => !stoppage.endTime && <StoppageStatusCell
            stoppage={stoppage}
            machineName={plant?.plantRef ?? ''}
            status={stoppage.stoppageSubType?.stoppageSubType ?? ''}
            setAsAvailable={setAsAvailable}
          />
        },
        {
          name: 'Breakdown Code',
          label: t("columns.breakdownCode"),
          editable: false,
          type: 'custom',
          value: stoppage.stoppageSubType?.stoppageSubType,
          tailwindClasses: 'w-full basis-[9.5%]',
          render: () => {
            return <BreakdownCodeCell
              stoppage={stoppage}
              handleStoppageSubTypeChange={handleStoppageSubTypeChange}
            />
          }
        },
        {
          name: 'Machine',
          label: i18n.t("fields.machine"),
          editable: false,
          sortable: true,
          type: 'custom',
          value: plant?.plantRef,
          tailwindClasses: 'w-full basis-[5%]',
          render: (tableValue) => <ZoneMachineCell name={tableValue.value} zone={plant?.zone} />
        },
        {
          name: 'Location',
          label: i18n.t("fields.location"),
          editable: false,
          type: 'custom',
          value: stoppage,
          tailwindClasses: 'w-full basis-[10%]',
          render: () => <StoppageLocationCell
            stoppage={stoppage}
            locationOptions={destinationOptions}
            isLocationOptionsLoading={isDestinationOptionsLoading}
            handleLocationChange={handleLocationChange}
          />
        },
        {
          name: 'Repairs/Faults',
          label: t("columns.repairsFaults"),
          editable: false,
          type: 'custom',
          value: stoppage.repairsFaults,
          tailwindClasses: 'w-full basis-[10%]',
          render: () => {
            return <RepairsFaultsCell
              stoppage={stoppage}
              handleRepairFaultsChange={saveRow}
            />
          }
        },
        {
          name: 'Work Order',
          label: t("columns.workOrder"),
          editable: true,
          type: 'custom',
          value: stoppage.workOrder,
          tailwindClasses: 'w-full basis-[8%]',
          render: () => {
            return <WorkOrderCell
              stoppage={stoppage}
              handleWorkOrderChange={saveRow}
            />
          }
        },
        {
          name: 'Date In',
          label: t("columns.dateIn"),
          type: 'string',
          value: formatDate(stoppage.startTime),
          tailwindClasses: 'w-full basis-[5%]',
          render: (tableValue) => <div className="w-full flex justify-center">{tableValue.value}</div>
        },
        {
          name: 'Time In',
          label: t("columns.timeIn"),
          type: 'string',
          value: stoppage.startTime.toFormat('HH:mm'),
          tailwindClasses: 'w-full basis-[3%]',
          render: (tableValue) => <div className="w-full flex items-center justify-center">{tableValue.value}</div>
        },
        {
          name: 'Time Out',
          label: t("columns.timeOut"),
          type: 'string',
          value: (() => {
            if (!stoppage.endTime || stoppage.endTime.toMillis() > shiftEndTime.toMillis()) return '';
            return stoppage.endTime.toFormat("HH:mm");
          })(),
          tailwindClasses: 'w-full basis-[3%]'
        },
        {
          name: 'Down Hrs',
          label: t("columns.downHours"),
          editable: false,
          type: 'custom',
          value: `${stoppage.startTime.toFormat("HH:mm")}|${selectedDate}|${selectedShift}`,
          tailwindClasses: 'w-full basis-[4%]',
          render: () => {
            const naturalEnd = stoppage.endTime ?? DateTime.now();
            const startedAt = stoppage.startTime;
            const endedAt = DateTime.min(naturalEnd, shiftEndTime);
            if (endedAt <= startedAt) return <span>{t("list.zeroDownHours")}</span>;

            return (
              <div className="flex w-full h-full text-semibold bg-theme-warning justify-center items-center flex-wrap gap-1 font-bold">
                <TimeCounter startedAt={startedAt} endedAt={endedAt} decimals={1} />
              </div>
            );
          }
        },
        {
          name: 'This Shift',
          label: t("columns.thisShift"),
          editable: true,
          type: 'custom',
          value: stoppage.startTime,
          tailwindClasses: 'w-full basis-[4.5%]',
          render: () => {
            const now = DateTime.now();
            const naturalEnd = stoppage.endTime ?? now;

            const clampedStart = DateTime.max(stoppage.startTime, shiftStartTime);
            const clampedEnd = DateTime.min(naturalEnd, shiftEndTime);

            if (clampedEnd <= clampedStart) {
              return <span>0.0</span>;
            }

            return (
              <TimeCounter
                startedAt={clampedStart}
                endedAt={clampedEnd}
                decimals={1}
              />
            );
          }
        },
        {
          name: 'Incident',
          label: t("columns.incident"),
          editable: true,
          type: 'custom',
          value: stoppage.incident,
          tailwindClasses: 'w-full basis-[2%]',
          render: () => {
            return (
              stoppage.incident ?
                <FontAwesomeIcon icon={faCircleCheck} className="text-theme-success" size="xl" /> :
                <FontAwesomeIcon icon={faCircleXmark} className="text-theme-danger" size="xl" />
            )
          }
        },
        {
          name: 'Who worked on it',
          label: t("columns.whoWorkedOnIt"),
          editable: true,
          type: 'custom',
          value: stoppage.whoWorkedOnIt,
          tailwindClasses: 'w-full basis-[9%]',
          render: () => {
            return (
              <WhoWorkedOnItCell
                stoppage={stoppage}
                handleWhoWorkedOnItChange={saveRow}
              />
            )
          }
        },
        {
          name: 'Booked Up by',
          label: t("columns.bookedUpBy"),
          editable: true,
          type: 'string',
          value: stoppage.bookedUpBy,
          tailwindClasses: 'w-full basis-[6%]',
          disabled: isReadOnly,
          onBlur: async (_, newValue) => {
            if (isReadOnly) return;
            await saveRow({
              ...stoppage,
              bookedUpBy: newValue
            });
          }
        },
        ...(telemetryEnabled ? [{
          name: 'Source',
          label: i18n.t("fields.source"),
          type: 'custom' as const,
          value: stoppage.isVerified === false ? 'Needs checking' : '',
          tailwindClasses: 'w-full basis-[6%]',
          render: () => (
            <div className="flex items-center justify-center gap-1.5 w-full">
              <div className="text-theme-text-muted text-xs">
                {detectionSourceLabel(stoppage.detectionSource)}
              </div>
              {/* Note: read-only here. There is no per-row accept endpoint on this screen - the trucks
                  table clears a whole hour at once and Stoppages clears by editing - so the mark reports
                  state without pretending to be a control. */}
              {isAutoDetected(stoppage.detectionSource) && (
                <VerificationIndicator isVerified={stoppage.isVerified !== false} subject={t("list.verificationSubject")} />
              )}
            </div>
          )
        }] : []),
        {
          name: 'Actions',
          label: i18n.t("fields.actions"),
          type: 'custom',
          value: 'custom',
          tailwindClasses: 'w-full basis-[8%]',
          render: () =>
            <div className="flex h-full w-full justify-center flex-nowrap gap-1 p-1">
              <UploadStoppageImageButton
                stoppage={stoppage}
                onStoppageUpdated={handleStoppageImageStateUpdated}
              />
              <ViewStoppageImagesButton
                stoppage={stoppage}
                onStoppageUpdated={handleStoppageImageStateUpdated}
              />
            </div>
        }
      ]
    };
  }, [
    selectedDate,
    selectedShift,
    isReadOnly,
    setAsAvailable,
    handleStoppageSubTypeChange,
    destinationOptions,
    isDestinationOptionsLoading,
    handleLocationChange,
    saveRow,
    shiftEndTime,
    shiftStartTime,
    handleStoppageImageStateUpdated,
    telemetryEnabled,
    t
  ]);

  const displayData = useMemo(() => rows.map(translateRows), [rows, translateRows]);

  const { pulseRowKey } = useScrollAndPulseRow(
    highlightedStoppageId ? String(highlightedStoppageId) : null,
    !isLoading,
    useCallback(() => setHighlightedStoppageId(null), [])
  );

  const localSelectedShiftStartTime = useRef(shiftStartTime);
  const localSelectedShiftEndTime = useRef(shiftEndTime);

  useEffect(() => {
    localSelectedShiftStartTime.current = shiftStartTime;
    localSelectedShiftEndTime.current = shiftEndTime;
  }, [shiftStartTime, shiftEndTime]);

  const handleStoppageUpdated = useCallback((updatedRow: StoppageDto) => {
    const stoppageIdToUpdate = updatedRow.stoppageId;

    if (stoppageIdToUpdate) {
      if (updatedRow.startTime >= localSelectedShiftEndTime.current || (updatedRow.endTime && updatedRow.endTime < localSelectedShiftStartTime.current)) {
        removeRowSource(oldRow => oldRow.stoppageId === stoppageIdToUpdate);
        enqueueSnackbar(t("messages.updatedInPastShift", { machine: updatedRow.plant?.plantRef }), { variant: 'info', autoHideDuration: 5000 });
        return;
      }
      updateRowSource(oldRow => oldRow.stoppageId === updatedRow.stoppageId, updatedRow);
      enqueueSnackbar(t("messages.machineUpdated", { machine: updatedRow.plant?.plantRef }), { variant: 'info', autoHideDuration: 5000 });
    }
  }, [updateRowSource, enqueueSnackbar, removeRowSource, t]);

  const handleStoppageAdded = useCallback((newStoppage: StoppageDto) => {
    if (!newStoppage || !newStoppage.stoppageId || newStoppage.startTime >= localSelectedShiftEndTime.current || (newStoppage.endTime && newStoppage.endTime < localSelectedShiftStartTime.current)) return;

    appendRowSource(newStoppage);
    enqueueSnackbar(t("messages.added", { machine: newStoppage.plant?.plantRef }), { variant: 'info', autoHideDuration: 5000 });
  }, [appendRowSource, enqueueSnackbar, t]);

  const handleStoppageDeleted = useEffectEvent((deletedStoppageId: number) => {
    if (!deletedStoppageId || deletedStoppageId <= 0) return;

    const deletedStoppage = fetchedRows?.find(row => row.stoppageId === deletedStoppageId);
    if (!deletedStoppage) return;
    const plantName = deletedStoppage.plant?.plantRef;

    removeRowSource(oldRow => oldRow.stoppageId === deletedStoppageId);
    enqueueSnackbar(plantName ? t("messages.deletedFor", { machine: plantName }) : t("messages.deleted"), { variant: 'info', autoHideDuration: 5000 });
  });

  useEffect(() => {
    if (!connection) return;
    const onStoppageDeleted = (deletedStoppageId: number) => handleStoppageDeleted(deletedStoppageId);
    connection.on("DataUpdatedAsync", handleStoppageUpdated);
    connection.on("DataInsertedAsync", handleStoppageAdded);
    connection.on("DataDeletedAsync", onStoppageDeleted);

    return () => {
      connection.off("DataUpdatedAsync", handleStoppageUpdated);
      connection.off("DataInsertedAsync", handleStoppageAdded);
      connection.off("DataDeletedAsync", onStoppageDeleted);
    };
  }, [connection, handleStoppageAdded, handleStoppageUpdated]);

  return (
    <TablePage>
      <Breadcrumbs>
        <DateSelectCrumb
          selectedDate={selectedDate}
          setSelectedDate={date => {
            dispatch(setSelectedDate(date.toISODate()!))
          }}
        />
        <ShiftSelectCrumb
          selectedShift={selectedShift}
          setSelectedShift={shift => dispatch(setSelectedShift(shift))}
        />
        <ButtonCrumb buttonText={t("list.crumb")} />
      </Breadcrumbs>
      <DataTable
        data={displayData}
        isLoading={isLoading}
        isBackgroundFetching={isBackgroundFetching}
        pulseRowKey={pulseRowKey}
        error={error}
        pageNumber={pageNumber}
        setPage={setPage}
        pageSize={pageSize}
        totalCount={totalCount}
        sortField={sortField}
        sortDirection={sortDirection}
        minTableWidthPixels={1920}
        disableContainer={true}
        fillHeight
        autoPageSize
        onAutoPageSize={onAutoPageSize}
        estimatedRowHeightPx={52}
        tableHeaderContent={
          <StoppagesTableHeader
            selectedStatus={selectedStatus}
            setSelectedStatus={setSelectedStatus}
            selectedJobType={selectedJobType}
            setSelectedJobType={setSelectedJobType}
            jobTypeOptions={[...jobTypes].sort((a, b) => a.label.localeCompare(b.label))}
            isJobTypeOptionsLoading={isJobTypesLoading}
            machineFilter={machineFilter}
            onMachineFilterChange={setMachineFilter}
            selectedZone={selectedZone}
            setSelectedZone={setSelectedZone}
            zoneOptions={zoneOptions}
            isZoneOptionsLoading={isZoneOptionsLoading}
            unverifiedOnly={unverifiedOnly}
            setUnverifiedOnly={setUnverifiedOnly}
            showNeedsChecking={telemetryEnabled}
          />
        }
      />
    </TablePage>
  )
};

export default Stoppages;